import type { BadgeTone, StatusRegistry } from '@flux/shared';
import { statusLabel, statusTone } from '@flux/shared';
import type { JSX, ReactNode } from 'react';
import { cn } from '@/lib/cn.js';

/**
 * Les 5 teintes de badge de `02-ecrans.md` §0, mappées aux tokens de
 * `index.css` (`--color-status-<tone>-*`, définis pour le clair et le sombre).
 * Seul point du code qui traduit un `BadgeTone` en classes Tailwind.
 */
const TONE_CLASS_NAMES: Record<BadgeTone, string> = {
  gray: 'bg-status-gray-bg text-status-gray-fg border-status-gray-border',
  blue: 'bg-status-blue-bg text-status-blue-fg border-status-blue-border',
  green: 'bg-status-green-bg text-status-green-fg border-status-green-border',
  orange: 'bg-status-orange-bg text-status-orange-fg border-status-orange-border',
  red: 'bg-status-red-bg text-status-red-fg border-status-red-border',
};

export interface BadgeProps {
  tone: BadgeTone;
  children: ReactNode;
  className?: string | undefined;
}

/** Badge coloré générique — préférer `StatusBadge` quand le contenu est un statut métier. */
export function Badge({ tone, children, className }: BadgeProps): JSX.Element {
  return (
    <span
      className={cn(
        'inline-flex h-5 max-w-full shrink-0 items-center gap-1 whitespace-nowrap rounded-md border px-1.5 text-xs font-medium leading-none tabular-nums',
        TONE_CLASS_NAMES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

export interface StatusBadgeProps<TCode extends string> {
  /** Registre `@flux/shared` (`ORDER_STATUS`, `MARTELET_STATE`, `STORE_MODE`…). */
  registry: StatusRegistry<TCode>;
  code: TCode;
  className?: string | undefined;
}

/**
 * Badge de statut métier : le libellé français et la teinte viennent
 * exclusivement du registre `@flux/shared` passé en prop — jamais choisis ici
 * (contrat de la vague, voir `README-shell.md`).
 */
export function StatusBadge<TCode extends string>({
  registry,
  code,
  className,
}: StatusBadgeProps<TCode>): JSX.Element {
  return (
    <Badge tone={statusTone(registry, code)} className={className}>
      {statusLabel(registry, code)}
    </Badge>
  );
}
